import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { api } from '../api';
import toast from 'react-hot-toast';
import { UserPlus, Eye, EyeOff } from 'lucide-react';
import { saveAuth } from '../auth';
import LegalModal from '../components/LegalModal';

const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #eef2ff 0%, #e0f2fe 100%);
  padding: 2rem 1rem;
`;

const Card = styled.div`
  width: 100%;
  max-width: 460px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(15, 23, 42, 0.15);
  padding: 2rem;
`;

const Title = styled.h1`
  font-size: 1.6rem;
  font-weight: 800;
  color: #111827;
  margin-bottom: 0.5rem;
`;

const Subtitle = styled.p`
  font-size: 0.95rem;
  color: #6b7280;
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  font-size: 0.9rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.25rem;
`;

const Input = styled.input`
  width: 100%;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  padding: 0.65rem 0.8rem;
  font-size: 0.95rem;
  margin-bottom: 1rem;
  &:focus {
    outline: none;
    border-color: #6366f1;
    box-shadow: 0 0 0 1px rgba(99, 102, 241, 0.3);
  }
`;

const PasswordWrapper = styled.div`
  position: relative;
  margin-bottom: 1rem;
`;

const PasswordInput = styled(Input)`
  margin-bottom: 0;
  padding-right: 2.4rem;
`;

const TogglePasswordButton = styled.button`
  position: absolute;
  right: 0.65rem;
  top: 50%;
  transform: translateY(-50%);
  border: none;
  background: transparent;
  padding: 0;
  margin: 0;
  cursor: pointer;
  color: #9ca3af;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CheckboxRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  font-size: 0.85rem;
  color: #4b5563;
`;

const Checkbox = styled.input`
  margin-top: 0.15rem;
`;

const KvkkLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  color: #4f46e5;
  text-decoration: underline;
  cursor: pointer;
  font: inherit;
`;

const Button = styled.button`
  width: 100%;
  border: none;
  border-radius: 999px;
  padding: 0.75rem 1rem;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  color: white;
  font-weight: 600;
  font-size: 0.95rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  cursor: pointer;
  margin-top: 0.75rem;
`;

const FooterText = styled.p`
  font-size: 0.85rem;
  color: #6b7280;
  margin-top: 1rem;
  text-align: center;
`;

const FooterLink = styled(Link)`
  color: #4f46e5;
  font-weight: 600;
  text-decoration: none;
`;

const Register = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [kvkk, setKvkk] = useState(false);
  const [loading, setLoading] = useState(false);
  const [kvkkModalOpen, setKvkkModalOpen] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!name || !email || !password || !passwordConfirm) {
      toast.error('İsim, e-posta ve şifre zorunlu');
      return;
    }
    if (password !== passwordConfirm) {
      toast.error('Şifreler birbiriyle eşleşmiyor');
      return;
    }
    if (!kvkk) {
      toast.error('KVKK onayını işaretlemeniz gerekiyor');
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/api/auth/register', { email, password, name });

      if (res.data?.requiresActivation) {
        toast.success(
          'Kayıt alındı. Hesabını aktifleştirmek için e-posta kutunu kontrol et.'
        );
        navigate('/login');
      } else if (res.data?.token) {
        // Eski davranışla uyumlu kalsın diye, backend ileride değişirse
        saveAuth(res.data.token, res.data.user);
        toast.success('Kayıt başarılı, giriş yapıldı');
        navigate('/dashboard');
      } else {
        toast.success('Kayıt tamamlandı.');
        navigate('/login');
      }
    } catch (err) {
      const message =
        err.response?.data?.error || 'Kayıt sırasında bir hata oluştu';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContainer>
      <Card>
        <Title>Kayıt Ol</Title>
        <Subtitle>
          İşHazır hesabını oluştur, CV ve kariyer araçlarını kullanmaya başla.
        </Subtitle>
        <form onSubmit={handleSubmit}>
          <div>
            <Label>İsim Soyisim</Label>
            <Input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Örn: Sultan Ilgaz"
            />
          </div>
          <div>
            <Label>E-posta</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="ornek@mail.com"
            />
          </div>
          <div>
            <Label>Şifre</Label>
            <PasswordWrapper>
              <PasswordInput
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="En az 6 karakter"
              />
              <TogglePasswordButton
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label="Şifreyi göster/gizle"
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </TogglePasswordButton>
            </PasswordWrapper>
          </div>
          <div>
            <Label>Şifre (tekrar)</Label>
            <PasswordWrapper>
              <PasswordInput
                type={showPassword ? 'text' : 'password'}
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                placeholder="Şifreyi tekrar girin"
              />
              <TogglePasswordButton
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label="Şifreyi göster/gizle"
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </TogglePasswordButton>
            </PasswordWrapper>
          </div>
          <CheckboxRow>
            <Checkbox
              id="kvkk"
              type="checkbox"
              checked={kvkk}
              onChange={(e) => setKvkk(e.target.checked)}
            />
            <label htmlFor="kvkk">
              <KvkkLink type="button" onClick={() => setKvkkModalOpen(true)}>
                KVKK aydınlatma metnini
              </KvkkLink>{' '}
              okudum ve kişisel verilerimin İşHazır kapsamında işlenmesini kabul ediyorum.
            </label>
          </CheckboxRow>
          <Button type="submit" disabled={loading}>
            <UserPlus size={18} />
            {loading ? 'Kayıt yapılıyor...' : 'Ücretsiz Kayıt Ol'}
          </Button>
        </form>
        <FooterText>
          Zaten hesabın var mı?{' '}
          <FooterLink to="/login">Giriş yap</FooterLink>
        </FooterText>
      </Card>
      <LegalModal
        open={kvkkModalOpen}
        onClose={() => setKvkkModalOpen(false)}
        title="KVKK Aydınlatma Metni"
      >
        <p>
        İSHAZIR.COM – KVKK AYDINLATMA METNİ (GÜNCEL)

Aydınlatma Metni
6698 Sayılı Kişisel Verilerin Korunması Kanunu (“KVKK”) Kapsamında

Bu aydınlatma metni, ishazir.com platformu üzerinden sunulan yapay zekâ destekli CV analiz hizmeti kapsamında işlenen kişisel verilere ilişkin olarak KVKK’nın 10. maddesi uyarınca hazırlanmıştır.

Platformun veri sorumlusu Doğukan Ilgaz’dır.

1. Veri Sorumlusunun Kimliği

Veri Sorumlusu: Doğukan Ilgaz
Adres: İstanbul / Küçükçekmece
E-posta: info@ishazir.com

İshazir.com bir şirket tüzel kişiliği değil, bireysel hizmet sağlayıcı tarafından işletilen dijital bir platformdur.

2. İşlenen Kişisel Veriler

Kullanıcı tarafından yüklenen CV dosyası

CV içeriğindeki bilgiler

E-posta adresi (kullanıcı iletişimi)

Teknik veriler: IP adresi, tarayıcı bilgisi, çerezler, oturum kayıtları

3. CV Verilerinin Saklanmaması

Yüklenen CV dosyaları sunucuda saklanmaz

CV içeriği işlendikten sonra otomatik olarak silinir

Yapay zekâ analizi OpenAI API üzerinden yapılır

Veri saklama, arşivleme veya üçüncü taraflara aktarım yapılmaz

Bu süreç KVKK’nın veri minimizasyonu ilkesine uygundur.

4. Kişisel Verilerin İşlenme Amaçları

CV analizinin yapay zekâ aracılığıyla gerçekleştirilmesi

Kullanıcıya analiz sonuçlarının sunulması

Güvenlik süreçleri (log kayıtları, kötüye kullanımı önleme)

Kullanıcı destek süreçlerinin yürütülmesi

Açık rıza alınması hâlinde bilgilendirme, geri bildirim ve gelişim duyuruları

5. Kişisel Verilerin Toplanma Yöntemi ve Hukuki Sebepler

Veriler:

Kullanıcı yüklemeleri

Form verileri

Tarayıcı tarafından aktarılan teknik bilgiler

yoluyla alınır.

Hukuki sebep:

KVKK m.5/2(c): Hizmet sunumu için zorunlu veri işleme

KVKK m.5/2(f): Meşru menfaat (güvenlik, log kayıtları)

KVKK m.5/1: Açık rıza gereken durumlar

6. Kişisel Verilerin Aktarılması
OpenAI API (yurt dışına teknik aktarım)

CV içeriği, yalnızca analiz işleminin yapılabilmesi için OpenAI API’ye iletilir.
Bu aktarım:

Sadece analiz amacıyla kullanılır

Saklama yapılmaz

OpenAI veri politikalarına uygundur

Kullanıcı hizmeti kullanarak bu teknik aktarımı kabul etmiş sayılır.

Resmî kurumlar

Sadece hukuken zorunlu hâllerde paylaşım yapılabilir.

7. KVKK Kapsamındaki Haklarınız

Kullanıcılar:

İşlenip işlenmediğini öğrenme

Bilgi talep etme

Silme/düzeltme talep etme

İtiraz hakkı

Tazminat talebi

haklarına sahiptir.

8. Başvuru Yöntemi

KVKK kapsamındaki tüm talepler:

📩 info@ishazir.com

adresine iletilebilir.
Başvurular 30 gün içinde sonuçlandırılır.
        </p>
      </LegalModal>
    </PageContainer>
  );
};

export default Register;

